import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Stack,
  Text,
  Group,
  Box,
  Title,
  useMantineColorScheme,
  UnstyledButton,
  Card,
  Table,
  Badge,
  Modal,
} from '@mantine/core';
import {
  IconUser,
  IconCreditCard,
  IconShield,
  IconChevronRight,
} from '@tabler/icons-react';
import PersonalInfoContent from '../components/settings/PersonalInfoContent';
import PrivacySecurityContent from '../components/settings/PrivacySecurityContent';
import BillingContent from '../components/settings/BillingContent';
import { notifications } from '@mantine/notifications';

interface SettingSectionProps {
  icon: React.ComponentType<{ size?: number }>;
  label: string;
  section: string;
  isActive: boolean;
  onClick: (section: string) => void;
}

const SettingSection: React.FC<SettingSectionProps> = ({
  icon: Icon,
  label,
  section,
  isActive,
  onClick,
}) => {
  const { colorScheme } = useMantineColorScheme();

  return (
    <Box
      p="md"
      style={{
        backgroundColor: isActive
          ? (colorScheme === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(160, 142, 115, 0.3)')
          : 'transparent',
        border: isActive
          ? `1px solid ${colorScheme === 'dark' ? 'rgba(59, 130, 246, 0.4)' : 'rgba(160, 142, 115, 0.5)'}`
          : '1px solid transparent',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
      }}
      onClick={() => onClick(section)}
      onMouseEnter={(e) => {
        if (!isActive) {
          e.currentTarget.style.backgroundColor = colorScheme === 'dark'
            ? 'rgba(55, 65, 81, 0.3)'
            : 'rgba(242, 237, 230, 0.5)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          e.currentTarget.style.backgroundColor = 'transparent';
        }
      }}
    >
      <Group justify="flex-start" align="center" gap="sm" wrap="nowrap">
        <Box style={{
          color: isActive ? '#ffffff' : (colorScheme === 'dark' ? '#e2e8f0' : '#5a5550'),
          flexShrink: 0
        }}>
          <Icon size={20} />
        </Box>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text
            fw={500}
            size="sm"
            style={{
              color: isActive ? '#ffffff' : (colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26'),
              fontFamily: 'Outfit, Inter, sans-serif',
              lineHeight: 1.3,
              textAlign: 'left',
            }}
          >
            {label}
          </Text>
        </Box>
        <Box style={{
          color: isActive ? '#ffffff' : (colorScheme === 'dark' ? '#94a3b8' : '#5a5550'),
          flexShrink: 0
        }}>
          <IconChevronRight size={16} />
        </Box>
      </Group>
    </Box>
  );
};

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { section } = useParams<{ section?: string }>();
  const { colorScheme } = useMantineColorScheme();

  // Determinar la sección activa desde los parámetros de la URL
  const getCurrentSection = () => {
    // Compatibilidad: si viene 'subscription', tratarlo como 'billing'
    if (section === 'subscription') return 'billing';
    return section || 'profile'; // default
  };

  const [activeSection, setActiveSection] = useState(getCurrentSection());

  // Sincronizar el estado con los parámetros de la URL
  useEffect(() => {
    setActiveSection(getCurrentSection());
  }, [section]);

  const settingSections = [
    {
      icon: IconUser,
      label: 'Información Personal',
      section: 'profile',
    },
    {
      icon: IconShield,
      label: 'Privacidad y Seguridad',
      section: 'privacy',
    },
    {
      icon: IconCreditCard,
      label: 'Facturación y Suscripciones',
      section: 'billing',
    },
  ];

  const handleSectionClick = (section: string) => {
    setActiveSection(section);
    if (section === 'profile') {
      navigate('/settings');
    } else {
      navigate(`/settings/${section}`);
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return <PersonalInfoContent />;
      case 'privacy':
        return <PrivacySecurityContent />;
      case 'subscription':
      case 'billing':
        return <BillingContent />;
      default:
        return <PersonalInfoContent />;
    }
  };

  return (
    <Container size="xl" py="md" style={{ paddingTop: '2rem' }}>
      <Box
        style={{
          display: 'flex',
          gap: '2rem',
          minHeight: 'calc(100vh - 120px)',
        }}
      >
        {/* Settings Sidebar */}
        <Box
          style={{
            width: '240px',
            flexShrink: 0,
          }}
        >
          <Box
            style={{
              backgroundColor: colorScheme === 'dark'
                ? 'rgba(30, 41, 59, 0.7)'
                : 'rgba(247, 243, 238, 0.9)',
              border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
              borderRadius: '16px',
              position: 'sticky',
              top: '2rem',
              height: 'fit-content',
            }}
          >
            <Box p="lg">
              <Text
                size="lg"
                fw={700}
                mb="lg"
                style={{
                  fontFamily: 'Space Grotesk, Inter, sans-serif',
                  color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
                }}
              >
                Configuración
              </Text>

              <Stack gap="sm">
                {settingSections.map((section) => (
                  <SettingSection
                    key={section.section}
                    icon={section.icon}
                    label={section.label}
                    section={section.section}
                    isActive={activeSection === section.section}
                    onClick={handleSectionClick}
                  />
                ))}
              </Stack>
            </Box>
          </Box>
        </Box>

        {/* Contenido Principal */}
        <Box style={{ flex: 1, marginTop: '1rem' }}>
          {renderContent()}
        </Box>
      </Box>

      {/* Mini footer */}
      <Box
        style={{
          marginTop: '3rem',
          paddingTop: '1rem',
          borderTop: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.5)'}`,
          textAlign: 'center',
        }}
      >
        <Text
          size="xs"
          style={{
            color: colorScheme === 'dark' ? '#94a3b8' : '#5a5550',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          © 2024 ENARM360. Todos los derechos reservados.
        </Text>
      </Box>
    </Container>
  );
};

export default SettingsPage;